import axios, { type AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import { ErrorBoundary, ErrorCard, LoadingCard } from "../../errors";
import { userValidation } from "../../errors";
import UserProfile from "./UserProfile";

const validateUserIdSafe = (id: string): boolean => {
	try {
		return userValidation.validateUserId(id);
	} catch (error) {
		console.error("Error validating user ID:", error);
		return false;
	}
};


const UserProfileWithErrorBoundary = () => {
	const { id = "" } = useParams<{ id: string }>();
	const authHeader = useAuthHeader();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [userError, setUserError] = useState<Error | null>(null);
	
	useEffect(() => {
		const searchParams = window.location.search;
		if (searchParams) {
			console.warn("URL contains query parameters which might cause issues:", searchParams);
		}
	}, []);
	
	useEffect(() => {
		if (!id || !authHeader) {
			return;
		}
		
		if (!validateUserIdSafe(id)) {
			setUserError(new Error("Invalid user ID format"));
			setIsLoading(false);
			return;
		}
		
		const controller = new AbortController();
		
		const checkUserExists = async () => {
			try {
				const response = await axios.get(
					apiRoutes.getUser(0, id, "", ""),
					{ 
						headers: authHeader,
						signal: controller.signal
					}
				);
				
				if (!response || !response.data) {
					throw new Error("User not found or invalid data received");
				}
				
				setIsLoading(false);
			} catch (error) {
				const axiosError = error as AxiosError;
				if (!axios.isCancel(axiosError)) {
					setUserError(new Error("User not found or unavailable"));
					setIsLoading(false);
				}
			}
		};
		
		checkUserExists();
		
		return () => {
			controller.abort();
		};
	}, [id, authHeader]);

	if (isLoading) {
		return (
			<LoadingCard
				title="Loading User Profile"
				message="Please wait while we load the user profile..."
				subMessage="If this takes more than a few seconds, the user may not exist."
			/>
		);
	}

	if (userError) {
		return (
			<ErrorCard 
				title="User Profile Error"
				message={userError.message}
				returnPath="/settings/users"
				returnButtonText="Return to Users"
			/>
		);
	}

	return (
		<ErrorBoundary parentPath="/settings/users">
			<UserProfile />
		</ErrorBoundary>
	);
};

export default UserProfileWithErrorBoundary;
